import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { stcBinSizeService } from '../../../../../services/stcBinSizeService';
import { StcBinSizeDto } from '../../../../../types/stcBinSize';

interface SelectedRows {
    [key: string]: boolean;
}

interface BinSizeSidePanelProps {
    selectedRows: SelectedRows;
    data: StcBinSizeDto[];
    onClose: () => void;
    onSave: () => void;
}

interface BulkEditConfig {
    [key: string]: {
        enabled: boolean;
        value: any;
    };
}

export const BinSizeSidePanel: React.FC<BinSizeSidePanelProps> = ({
    selectedRows,
    data,
    onClose,
    onSave
}) => {
    const [bulkEditConfig, setBulkEditConfig] = useState<BulkEditConfig>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;
    const selectedData = data.filter(item => selectedRows[item.id.toString()]);
    const isSingleSelection = selectedRowCount === 1;
    const singleItem = isSingleSelection ? selectedData[0] : null;

    const fields = ['binSizeNo', 'description1', 'width', 'depth', 'height', 'totalVolume', 'unlimitedYesNo', 'externalSystemID'];

    // Initialize bulk edit config - pre-fill when single selection
    useEffect(() => {
        const initialConfig: BulkEditConfig = {};
        fields.forEach(field => {
            if (isSingleSelection && singleItem) {
                // Single selection: pre-fill with current values and enable all fields
                initialConfig[field] = {
                    enabled: true,
                    value: (singleItem as any)[field] ?? getDefaultValue(field)
                };
            } else {
                // Multi selection: empty defaults, disabled
                initialConfig[field] = {
                    enabled: false,
                    value: getDefaultValue(field)
                };
            }
        });
        setBulkEditConfig(initialConfig);
    }, []);

    const getDefaultValue = (field: string): any => {
        if (field === 'unlimitedYesNo') return false;
        if (field === 'description1') return '';
        if (field === 'externalSystemID') return undefined;
        return 0;
    };

    const getFieldLabel = (field: string): string => {
        const labels: { [key: string]: string } = {
            'binSizeNo': 'Størrelse nr.',
            'description1': 'Beskrivelse',
            'width': 'Bredde',
            'depth': 'Dybde',
            'height': 'Høyde',
            'totalVolume': 'Totalt volum',
            'unlimitedYesNo': 'Ubegrenset',
            'externalSystemID': 'Ekstern system ID'
        };
        return labels[field] || field;
    };

    const isSingleOnlyField = (field: string): boolean => {
        return field === 'binSizeNo';
    };

    const handleFieldToggle = (field: string, enabled: boolean) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                enabled
            }
        }));
    };

    const handleFieldValueChange = (field: string, value: any) => {
        let parsedValue = value;
        if (field === 'externalSystemID' || field === 'binSizeNo') {
            parsedValue = value !== '' && value !== undefined && value !== null ? parseInt(value) : undefined;
        } else if (['width', 'depth', 'height', 'totalVolume'].includes(field)) {
            parsedValue = value !== '' && value !== undefined && value !== null ? parseFloat(value) : 0;
        }
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                value: parsedValue
            }
        }));
    };

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            const enabledFields = Object.entries(bulkEditConfig)
                .filter(([, config]) => config.enabled)
                .map(([field, config]) => ({ field, value: config.value }));

            if (enabledFields.length === 0) {
                setError('Ingen felter valgt for oppdatering');
                return;
            }

            // binSizeNo can only be changed for single selection
            if (enabledFields.some(f => f.field === 'binSizeNo') && selectedRowCount > 1) {
                setError('Størrelse nr. kan kun endres for én lokasjonsstørrelse om gangen');
                return;
            }

            const updatePromises = selectedData.map(item => {
                const updateRequest: any = {
                    description1: item.description1,
                    width: item.width,
                    depth: item.depth,
                    height: item.height,
                    totalVolume: item.totalVolume,
                    unlimitedYesNo: item.unlimitedYesNo,
                    externalSystemID: item.externalSystemID
                };

                enabledFields.forEach(({ field, value }) => {
                    updateRequest[field] = value;
                });

                return stcBinSizeService.update(
                    item.companyNo,
                    item.binSizeNo,
                    updateRequest
                );
            });

            await Promise.all(updatePromises);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved oppdatering');
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const deletePromises = selectedData.map(item =>
                stcBinSizeService.delete(item.companyNo, item.binSizeNo)
            );

            await Promise.all(deletePromises);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved sletting');
        } finally {
            setIsDeleting(false);
        }
    };

    const enabledFieldCount = Object.values(bulkEditConfig).filter(config => config.enabled).length;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {isSingleSelection ? 'Rediger lokasjonsstørrelse' : 'Masseoppdatering'}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'} valgt i Lokasjonsstørrelser
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('edit')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'edit'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Rediger
                    </button>
                    <button
                        onClick={() => setActiveTab('delete')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'delete'
                                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Slett
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'edit' ? (
                        <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium mb-1">
                                            {isSingleSelection ? 'Redigering' : 'Masseoppdatering'}
                                        </p>
                                        <p>
                                            {isSingleSelection
                                                ? 'Alle felt er forhåndsutfylt med gjeldende verdier. Endre det du ønsker og lagre.'
                                                : 'Velg feltene du ønsker å oppdatere for alle valgte rader. Kun markerte felt vil bli endret.'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Field Editor */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-gray-900">Felter som skal oppdateres</h3>

                                {fields.map(field => {
                                    const config = bulkEditConfig[field];
                                    const singleOnly = isSingleOnlyField(field);
                                    const isDisabled = singleOnly && selectedRowCount > 1;

                                    if (!config) return null;

                                    return (
                                        <div
                                            key={field}
                                            className={`border rounded-lg p-3 transition-colors ${
                                                config.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            {/* Field Toggle */}
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={config.enabled}
                                                        onChange={(e) => handleFieldToggle(field, e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {getFieldLabel(field)}
                                                    </span>
                                                </label>
                                                {singleOnly && selectedRowCount > 1 && config.enabled && (
                                                    <span className="text-xs text-amber-600">Kun ved enkeltvalg</span>
                                                )}
                                            </div>

                                            {/* Field Value Input */}
                                            {config.enabled && (
                                                <div className="mt-2">
                                                    {field === 'unlimitedYesNo' ? (
                                                        <select
                                                            value={config.value ? 'true' : 'false'}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value === 'true')}
                                                            disabled={isDisabled}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                        >
                                                            <option value="false">Nei</option>
                                                            <option value="true">Ja</option>
                                                        </select>
                                                    ) : field === 'description1' ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                value={config.value}
                                                                onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                                disabled={isDisabled}
                                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                placeholder={`Ny verdi for ${getFieldLabel(field)}`}
                                                                maxLength={100}
                                                                required
                                                            />
                                                            <p className="text-xs text-gray-500 mt-1">Beskrivelse er obligatorisk</p>
                                                        </>
                                                    ) : field === 'externalSystemID' || field === 'binSizeNo' ? (
                                                        <input
                                                            type="number"
                                                            value={config.value ?? ''}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                            disabled={isDisabled}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            placeholder={`Ny verdi for ${getFieldLabel(field)}`}
                                                            min={field === 'binSizeNo' ? 1 : undefined}
                                                        />
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={config.value}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                            disabled={isDisabled}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            placeholder={`Ny verdi for ${getFieldLabel(field)}`}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary */}
                            {enabledFieldCount > 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium">Klar til å lagre</p>
                                            <p>
                                                {enabledFieldCount} felt vil bli oppdatert for {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium mb-1">Massesletting</p>
                                        <p>Du er i ferd med å slette {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}. Denne handlingen kan ikke angres.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Selected Items List */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-900">Rader som vil bli slettet:</h3>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                    {selectedData.map(item => (
                                        <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border">
                                            Størrelse {item.binSizeNo}: {item.description1}
                                            {` (${item.width} x ${item.depth} x ${item.height})`}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Avbryt
                        </button>

                        {activeTab === 'edit' ? (
                            <button
                                onClick={handleBulkUpdate}
                                disabled={enabledFieldCount === 0 || isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    enabledFieldCount === 0 || isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Lagrer...
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Lagret!
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Lagre endringer
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isDeleting
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Sletter...
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Slettet!
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Slett {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
